import {
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type PointerEvent,
  type Ref
} from "react";
import type { Lair, LairConnection, LairRoom } from "../types";
import { assetPath } from "../lib/urls";
import { gsap, useGSAP } from "../lib/animation";

interface LairMapProps {
  lair: Lair;
  selectedRoomId: string;
  accent: string;
  portrait?: string;
  svgRef?: Ref<SVGSVGElement>;
  onSelect: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
}

const VIEW_W = 1140;
const VIEW_H = 700;
/** vertical band reserved for rooms; the strip below holds the legend */
const ROOM_H = 620;

const roleLabels: Record<LairRoom["role"], string> = {
  approach: "Approach",
  entrance: "Entrance",
  chokepoint: "Chokepoint",
  quarters: "Quarters",
  "false-hoard": "False hoard",
  hoard: "Hoard",
  sanctum: "Sanctum",
  escape: "Escape",
  anomaly: "Anomaly"
};

const roleColor: Record<LairRoom["role"], string> = {
  approach: "#b7a06a",
  entrance: "#d8bd77",
  chokepoint: "#c98a47",
  quarters: "#a58f5e",
  "false-hoard": "#c98a47",
  hoard: "#efcf75",
  sanctum: "#e0653f",
  escape: "#90b4c2",
  anomaly: "#b68ad7"
};

const roleTier: Record<LairRoom["role"], number> = {
  approach: 0,
  entrance: 1,
  chokepoint: 1,
  quarters: 2,
  "false-hoard": 2,
  anomaly: 2,
  hoard: 3,
  sanctum: 3,
  escape: 4
};

const tierLabels = ["Approach", "Threshold", "Halls", "Heart", "Escape"];
const tierX = [9, 30, 51, 72, 91];

const dangerPips: Record<LairRoom["danger"], number> = {
  low: 1,
  moderate: 2,
  high: 3,
  mythic: 4
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function px(value: number): number {
  return (value / 100) * VIEW_W;
}

function py(value: number): number {
  return (value / 100) * ROOM_H;
}

function sealRadius(room: LairRoom): number {
  return 22 + room.radius * 3;
}

function truncate(value: string, max = 22): string {
  return value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;
}

function glyphPath(role: LairRoom["role"]): string {
  switch (role) {
    case "hoard":
    case "false-hoard":
      return "M-5.5 2.2 A2.6 2.6 0 1 1 -5.4 2.1 M2.6 -1.6 A2.6 2.6 0 1 1 2.7 -1.7 M-7 5 H7";
    case "sanctum":
      return "M0 -8 L2.2 -2.4 8 0 L1.8 2 L0.2 8 L-2.2 2.4 -8 0 L-2 -2 Z";
    case "escape":
      return "M-6 5.2 L6 -5.2 M1.4 -6 H6 V-1.6";
    case "chokepoint":
      return "M-6.6 -6.4 L0 -1.4 6.6 -6.4 M-6.6 6.4 L0 1.4 6.6 6.4";
    case "entrance":
    case "approach":
      return "M-6 6 V-1.6 A6 6 0 0 1 6 -1.6 V6 M-8 6 H8";
    case "anomaly":
      return "M0 -7.2 v14.4 M-7.2 0 h14.4 M-4.8 -4.8 l9.6 9.6 M4.8 -4.8 l-9.6 9.6";
    default:
      return "M-6 -4.8 h12 v9.6 h-12 Z M0 -4.8 v9.6";
  }
}

interface RoutePoints {
  d: string;
  connection: LairConnection;
}

function routePath(from: LairRoom, to: LairRoom): string {
  const a = { x: px(from.x), y: py(from.y) };
  const b = { x: px(to.x), y: py(to.y) };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distance = Math.hypot(dx, dy) || 1;
  const trimA = sealRadius(from) + 6;
  const trimB = sealRadius(to) + 6;
  const p1 = { x: a.x + (dx / distance) * trimA, y: a.y + (dy / distance) * trimA };
  const p2 = { x: b.x - (dx / distance) * trimB, y: b.y - (dy / distance) * trimB };
  const mx = (p1.x + p2.x) / 2 + (p2.y - p1.y) * 0.12;
  const my = (p1.y + p2.y) / 2 + (p1.x - p2.x) * 0.12;
  return `M ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} Q ${mx.toFixed(1)} ${my.toFixed(1)} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
}

export function LairMap({
  lair,
  selectedRoomId,
  accent,
  portrait,
  svgRef,
  onSelect,
  onMove
}: LairMapProps) {
  const [dragged, setDragged] = useState<string | null>(null);
  const localSvgRef = useRef<SVGSVGElement | null>(null);
  const setSvgRefs = useCallback(
    (node: SVGSVGElement | null) => {
      localSvgRef.current = node;
      if (!svgRef) return;
      if (typeof svgRef === "function") {
        svgRef(node);
      } else {
        (svgRef as MutableRefObject<SVGSVGElement | null>).current = node;
      }
    },
    [svgRef]
  );
  const id = useId().replace(/:/g, "");
  const roomLookup = useMemo(
    () => new Map(lair.rooms.map((room) => [room.id, room])),
    [lair.rooms]
  );
  const reduceMotion = useMemo(
    () =>
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );
  const routes = useMemo<RoutePoints[]>(
    () =>
      lair.connections.flatMap((connection) => {
        const from = roomLookup.get(connection.from);
        const to = roomLookup.get(connection.to);
        if (!from || !to) return [];
        return [{ d: routePath(from, to), connection }];
      }),
    [lair.connections, roomLookup]
  );
  const presentTiers = useMemo(() => {
    const tiers = new Set(lair.rooms.map((room) => roleTier[room.role]));
    return tierLabels.map((label, index) => ({ label, x: tierX[index], present: tiers.has(index) }));
  }, [lair.rooms]);
  const embers = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        x: 60 + ((index * 197) % 1020),
        top: 90 + ((index * 53) % 140),
        r: 1 + ((index * 7) % 10) / 7,
        delay: ((index * 0.9) % 7).toFixed(1)
      })),
    []
  );

  useGSAP(
    () => {
      const root = localSvgRef.current;
      if (!root || reduceMotion) return;

      const routeNodes = gsap.utils.toArray<SVGPathElement>(".map-route", root);
      const seals = gsap.utils.toArray<SVGGElement>(".map-room", root);

      routeNodes.forEach((route, index) => {
        const length = route.getTotalLength();
        gsap.fromTo(
          route,
          { strokeDasharray: length, strokeDashoffset: length },
          {
            strokeDashoffset: 0,
            duration: 0.62 + index * 0.045,
            ease: "power2.out",
            clearProps: "strokeDasharray,strokeDashoffset"
          }
        );
      });

      gsap.from(seals, {
        autoAlpha: 0,
        scale: 0.82,
        transformOrigin: "center center",
        stagger: { amount: 0.34, from: "center" },
        duration: 0.58,
        ease: "back.out(1.35)"
      });
    },
    {
      scope: localSvgRef,
      dependencies: [lair.name, lair.terrain, lair.rooms.length, lair.connections.length],
      revertOnUpdate: true
    }
  );

  useGSAP(
    () => {
      const root = localSvgRef.current;
      if (!root || reduceMotion) return;

      const selectedShape = root.querySelector(".map-room.is-selected .map-room__shape");
      if (!selectedShape) return;

      gsap.fromTo(
        selectedShape,
        { scale: 0.94, transformOrigin: "center center" },
        {
          scale: 1.06,
          repeat: 1,
          yoyo: true,
          duration: 0.24,
          ease: "sine.inOut",
          clearProps: "scale"
        }
      );
    },
    { scope: localSvgRef, dependencies: [selectedRoomId] }
  );

  const updatePosition = (event: PointerEvent<SVGSVGElement>) => {
    if (!dragged) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = (((event.clientY - bounds.top) / bounds.height) * VIEW_H * 100) / ROOM_H;
    onMove(dragged, clamp(x, 6, 94), clamp(y, 12, 82));
  };

  return (
    <svg
      ref={setSvgRefs}
      className="lair-map"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      fontFamily="Georgia, 'Times New Roman', serif"
      role="img"
      aria-label={`${lair.name} interactive lair map`}
      onPointerMove={updatePosition}
      onPointerUp={() => setDragged(null)}
      onPointerLeave={() => setDragged(null)}
    >
      <defs>
        <linearGradient id={`${id}-gold-bevel`} x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor="#f7e3ae" />
          <stop offset=".28" stopColor="#d8a94e" />
          <stop offset=".55" stopColor="#6e4c1e" />
          <stop offset=".78" stopColor="#c99a45" />
          <stop offset="1" stopColor="#3c2a10" />
        </linearGradient>
        <linearGradient id={`${id}-gold-inner`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#3c2a10" />
          <stop offset=".45" stopColor="#a9812f" />
          <stop offset="1" stopColor="#f4d88e" />
        </linearGradient>
        <linearGradient id={`${id}-molten`} x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0" stopColor="#f4d88e" />
          <stop offset=".5" stopColor="#e0653f" />
          <stop offset="1" stopColor="#f4c96d" />
        </linearGradient>
        <radialGradient id={`${id}-inner-fire`} cx="0.5" cy="0.62" r="0.72">
          <stop offset="0" stopColor="#ffb469" stopOpacity=".5" />
          <stop offset=".55" stopColor="#8f2f1d" stopOpacity=".24" />
          <stop offset="1" stopColor="#000000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-lava-light`} cx="0.5" cy="1.08" r="0.7">
          <stop offset="0" stopColor="#e0653f" stopOpacity=".34" />
          <stop offset="1" stopColor="#e0653f" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-vignette`} cx="0.5" cy="0.5" r="0.72">
          <stop offset=".62" stopColor="#000000" stopOpacity="0" />
          <stop offset="1" stopColor="#000000" stopOpacity=".82" />
        </radialGradient>
        <linearGradient id={`${id}-portrait-fade`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#000000" stopOpacity="0" />
          <stop offset=".55" stopColor="#ffffff" stopOpacity=".65" />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
        <mask id={`${id}-portrait-mask`}>
          <rect x="440" y="0" width="700" height={VIEW_H} fill={`url(#${id}-portrait-fade)`} />
        </mask>
        <pattern
          id={`${id}-obsidian`}
          patternUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={VIEW_W}
          height={VIEW_H}
        >
          <image
            href={assetPath("assets/textures/obsidian-panel.webp")}
            x="0"
            y="0"
            width={VIEW_W}
            height={VIEW_H * 1.2}
            preserveAspectRatio="xMidYMid slice"
          />
        </pattern>
        <filter id={`${id}-soft`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <filter id={`${id}-soft2`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
        <filter id={`${id}-seal-shadow`} x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="5" stdDeviation="6" floodColor="#000000" floodOpacity=".75" />
        </filter>
      </defs>

      {/* table surface */}
      <rect width={VIEW_W} height={VIEW_H} fill="#0a0806" />
      <rect width={VIEW_W} height={VIEW_H} fill={`url(#${id}-obsidian)`} opacity=".92" />
      {portrait ? (
        <image
          href={assetPath(portrait)}
          x="440"
          y="0"
          width="700"
          height={VIEW_H}
          preserveAspectRatio="xMidYMid slice"
          mask={`url(#${id}-portrait-mask)`}
          opacity=".5"
        />
      ) : null}
      <rect width={VIEW_W} height={VIEW_H} fill={`url(#${id}-lava-light)`} />
      <rect width={VIEW_W} height={VIEW_H} fill={`url(#${id}-vignette)`} />

      {/* tier headers */}
      {presentTiers.map((tier) =>
        tier.present ? (
          <g key={tier.label} className="wt-tier">
            <text
              x={px(tier.x)}
              y="34"
              textAnchor="middle"
              fill="#6d6248"
              fontSize="19"
              letterSpacing="0.42em"
            >
              {tier.label.toUpperCase()}
            </text>
            <line
              x1={px(tier.x) - 34}
              x2={px(tier.x) + 34}
              y1="44"
              y2="44"
              stroke="rgba(201, 164, 85, 0.25)"
              strokeWidth="1"
            />
          </g>
        ) : null
      )}

      {/* molten routes */}
      <g>
        {routes.map(({ d, connection }) => {
          const active =
            connection.from === selectedRoomId || connection.to === selectedRoomId;
          const glow = connection.escape
            ? "#90b4c2"
            : connection.secret
              ? "#b68ad7"
              : "#e0873f";
          return (
            <g key={connection.id} className={active ? "wt-route is-active" : "wt-route"}>
              <path
                className="wt-route__groove"
                d={d}
                fill="none"
                stroke="rgba(0, 0, 0, 0.8)"
                strokeWidth="7"
                strokeLinecap="round"
                filter={`url(#${id}-soft2)`}
              />
              <path
                className="wt-route__glow"
                d={d}
                fill="none"
                stroke={glow}
                strokeWidth="6"
                strokeLinecap="round"
                opacity={active ? 0.7 : 0.35}
                filter={`url(#${id}-soft)`}
              />
              <path
                className={`map-route wt-route__core ${
                  connection.secret ? "wt-route__core--secret" : ""
                } ${connection.escape ? "wt-route__core--escape" : ""}`}
                d={d}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                stroke={
                  connection.escape
                    ? "#a8c8d4"
                    : connection.secret
                      ? "#c7a1e4"
                      : `url(#${id}-molten)`
                }
                strokeWidth={active ? 3.6 : connection.secret ? 1.6 : 2.6}
                strokeDasharray={
                  connection.secret ? "6 7" : connection.escape ? "1 8" : undefined
                }
              />
              {!connection.secret && !connection.escape && !reduceMotion ? (
                <path
                  className="wt-route__shimmer"
                  d={d}
                  fill="none"
                  stroke="#fff2c6"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeDasharray="2 26"
                  opacity="0.8"
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    values="0;-28"
                    dur="2.8s"
                    repeatCount="indefinite"
                  />
                </path>
              ) : null}
            </g>
          );
        })}
      </g>

      {/* seals */}
      {lair.rooms.map((room, index) => {
        const selected = selectedRoomId === room.id;
        const cx = px(room.x);
        const cy = py(room.y);
        const r = sealRadius(room);
        const color = roleColor[room.role];
        const glyphScale = r / 24;
        const glyphY = cy - r * 0.13;
        const pips = dangerPips[room.danger];
        return (
          <g
            key={room.id}
            className={`map-room wt-seal ${selected ? "is-selected" : ""}`}
            role="button"
            aria-label={`${room.name}, ${roleLabels[room.role]}`}
            tabIndex={0}
            onClick={() => onSelect(room.id)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") onSelect(room.id);
            }}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              setDragged(room.id);
              onSelect(room.id);
            }}
          >
            <title>{`${index + 1}. ${room.name} — ${roleLabels[room.role]}`}</title>
            <circle
              className="wt-seal__halo"
              cx={cx}
              cy={cy}
              r={r + 12}
              fill="none"
              stroke={accent}
              strokeWidth="2.5"
              opacity={selected ? 1 : 0}
              filter={`url(#${id}-soft)`}
            />
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="#0a0806"
              filter={`url(#${id}-seal-shadow)`}
            />
            <circle
              className="map-room__shape"
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={`url(#${id}-gold-bevel)`}
              strokeWidth="6.5"
            />
            <circle
              cx={cx}
              cy={cy}
              r={r - 5}
              fill="none"
              stroke={`url(#${id}-gold-inner)`}
              strokeWidth="1.6"
              opacity=".9"
            />
            <circle cx={cx} cy={cy} r={r + 3.6} fill="none" stroke="rgba(0,0,0,0.7)" strokeWidth="1.4" />
            <circle cx={cx} cy={cy} r={r - 6.5} fill={`url(#${id}-obsidian)`} />
            <circle cx={cx} cy={cy} r={r - 6.5} fill={`url(#${id}-inner-fire)`} />
            <circle
              cx={cx}
              cy={cy}
              r={r - 7.5}
              fill="none"
              stroke={selected ? accent : color}
              strokeWidth="1.2"
              opacity=".55"
            />
            <path
              d={glyphPath(room.role)}
              className="wt-seal__glyph-shadow"
              fill="none"
              stroke="#000000"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.8"
              transform={`translate(${cx} ${glyphY + 1.8}) scale(${glyphScale})`}
            />
            <path
              d={glyphPath(room.role)}
              className="wt-seal__glyph"
              fill="none"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              stroke={`url(#${id}-gold-inner)`}
              transform={`translate(${cx} ${glyphY}) scale(${glyphScale})`}
            />
            <circle
              cx={cx}
              cy={cy + r - 14}
              r="13"
              fill="#14100a"
              stroke={`url(#${id}-gold-bevel)`}
              strokeWidth="2.4"
            />
            <text
              className="wt-seal__number"
              x={cx}
              y={cy + r - 9}
              textAnchor="middle"
              fill="#f4d88e"
              fontSize="15"
            >
              {index + 1}
            </text>
            {Array.from({ length: pips }, (_, pip) => {
              const angle = -Math.PI / 2 + (pip - (pips - 1) / 2) * 0.38;
              return (
                <circle
                  key={pip}
                  className={`wt-seal__stud ${room.danger === "mythic" ? "wt-seal__stud--mythic" : ""}`}
                  cx={cx + Math.cos(angle) * r}
                  cy={cy + Math.sin(angle) * r}
                  r="4.5"
                  fill="#e0653f"
                  stroke="#2a0d05"
                  strokeWidth="1"
                />
              );
            })}
            <text
              className="wt-seal__name-shadow"
              x={cx}
              y={cy + r + 28.4}
              textAnchor="middle"
              fill="#000000"
              opacity="0.85"
              fontSize="16"
              letterSpacing="0.04em"
            >
              {truncate(room.name).toUpperCase()}
            </text>
            <text
              className="wt-seal__name"
              x={cx}
              y={cy + r + 27}
              textAnchor="middle"
              fill={`url(#${id}-gold-inner)`}
              fontSize="16"
              letterSpacing="0.04em"
            >
              {truncate(room.name).toUpperCase()}
            </text>
            <text
              className="wt-seal__role"
              x={cx}
              y={cy + r + 46}
              textAnchor="middle"
              fill="#97865f"
              fontSize="13"
              letterSpacing="0.3em"
            >
              {roleLabels[room.role].toUpperCase()}
            </text>
          </g>
        );
      })}

      {/* ember motes */}
      {!reduceMotion
        ? embers.map((ember, index) => (
            <circle
              key={index}
              className="wt-ember"
              cx={ember.x}
              cy={ROOM_H - 10}
              r={ember.r * 2}
              fill="#ffb469"
              opacity="0"
            >
              <animate
                attributeName="cy"
                values={`${ROOM_H - 10};${ember.top}`}
                dur="11s"
                begin={`${ember.delay}s`}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0;0.45;0"
                dur="11s"
                begin={`${ember.delay}s`}
                repeatCount="indefinite"
              />
            </circle>
          ))
        : null}

      {/* legend */}
      <g
        className="wt-legend"
        transform={`translate(22 ${VIEW_H - 26})`}
        fontSize="15"
        letterSpacing="0.1em"
        fill="#8f8266"
      >
        <path d="M0 0 H38" className="wt-legend__main" fill="none" stroke="#d8a94e" strokeWidth="3" strokeLinecap="round" />
        <text x="46" y="5">Molten route</text>
        <path d="M188 0 H226" className="wt-legend__secret" fill="none" stroke="#b68ad7" strokeWidth="3" strokeLinecap="round" strokeDasharray="5 5" />
        <text x="234" y="5">Secret vein</text>
        <path d="M362 0 H400" className="wt-legend__escape" fill="none" stroke="#90b4c2" strokeWidth="3" strokeLinecap="round" strokeDasharray="1 6" />
        <text x="408" y="5">Escape draft</text>
      </g>
    </svg>
  );
}
